import React, { useMemo } from 'react'
import cx from 'classnames'

import { FromText } from './FromText'
import { FromURL } from './FromURL'
import { FromFile } from './FromFile'
import { FromDoc } from './FromDoc'
import { ContentSourcePickerVariant } from './Types'

import styles from './ContentSourcePicker.module.scss'

export enum Importer {
  ScanWebsite = 'Scan Website',
  AddText = 'Add Text',
  SelectDoc = 'Select Doc',
  UploadFiles = 'Upload Files',
  PasteWebsiteText = 'Paste Website Text',
}

export type ContentSourcePickerProps = {
  selectedImporter: Importer
  setSelectedImporter: (importer: Importer) => void
  sampleText: string
  setSampleText: (sampleText: string) => void
  variant?: ContentSourcePickerVariant
  type?: 'campaign' | 'voice'
  selectedDoc?: { id: string; name: string; emoji: string | null }
}

export const ContentSourcePicker: React.FC<React.PropsWithChildren<ContentSourcePickerProps>> = ({
  selectedImporter,
  setSelectedImporter,
  sampleText,
  setSampleText,
  variant = ContentSourcePickerVariant.BrandVoiceImport,
  type,
  selectedDoc,
}) => {
  const importerComponent = useMemo(() => {
    // a token is, using a very crude approximation, equivalent to 4 characters
    // and GPT4 has a max context length of 8k tokens
    // so we limit the sample to 6k "tokens", leaving 2k for the prompt
    const onChangeHandler = (text: string) => setSampleText(text.slice(0, 6 * 1024 * 4))
    switch (selectedImporter) {
      case Importer.ScanWebsite:
        return <FromURL sampleText={sampleText} onChange={setSampleText} type={type} />
      case Importer.AddText:
        return <FromText sampleText={sampleText} onChange={setSampleText} variant={variant} />
      case Importer.SelectDoc:
        return <FromDoc onChange={onChangeHandler} selectedDoc={selectedDoc} />
      case Importer.UploadFiles:
        return <FromFile onChange={onChangeHandler} />
      default:
        return null
    }
  }, [sampleText, selectedDoc, selectedImporter, setSampleText, type, variant])

  return (
    <div className={cx(styles.ContentSourcePicker, styles[variant])}>
      <div className={styles.buttons}>
        <button
          className={cx(styles.button, { [styles.active]: selectedImporter === Importer.ScanWebsite })}
          onClick={() => {
            if (selectedImporter !== Importer.ScanWebsite) setSampleText('')
            setSelectedImporter(Importer.ScanWebsite)
          }}
        >
          {Importer.ScanWebsite}
        </button>

        <button
          className={cx(styles.button, { [styles.active]: selectedImporter === Importer.AddText })}
          onClick={() => {
            if (selectedImporter !== Importer.AddText) setSampleText('')
            setSelectedImporter(Importer.AddText)
          }}
        >
          {Importer.AddText}
        </button>

        <button
          className={cx(styles.button, { [styles.active]: selectedImporter === Importer.SelectDoc })}
          onClick={() => {
            if (selectedImporter !== Importer.SelectDoc) setSampleText('')
            setSelectedImporter(Importer.SelectDoc)
          }}
        >
          {Importer.SelectDoc}
        </button>

        <button
          className={cx(styles.button, { [styles.active]: selectedImporter === Importer.UploadFiles })}
          onClick={() => {
            if (selectedImporter !== Importer.UploadFiles) setSampleText('')
            setSelectedImporter(Importer.UploadFiles)
          }}
        >
          {Importer.UploadFiles}
        </button>
      </div>

      <div>{importerComponent}</div>
    </div>
  )
}
